import { LinearProgress } from "@material-ui/core";
import { isUndefined } from "lodash";
import { Subject, from, concat, Observable } from "rxjs";
import { map, takeUntil } from "rxjs/operators";
import styled from "styled-components";
import request from "superagent";
import { Spinner } from "./icon";
import { FileProgressLoaderProps, ResultWithProgress } from "./models";

const StyledLinearProgress = styled(LinearProgress)`
  width: 70%;
`;

export const FileProgressLoader: React.FunctionComponent<FileProgressLoaderProps> = ({
  percent,
}) =>
  isUndefined(percent) ? (
    <Spinner />
  ) : (
    <StyledLinearProgress variant="determinate" value={percent} />
  );

/**
 * This function augments a superagent post with progress. Intended to use with files.
 *
 * E.g. withProgress(addFieldsToForm(api.post(`v2/kyc/`), fields), (r) =>
 *    r
 *      .then((response) => response.body)
 *      .catch(processErrorFromPromise)
 *    );
 *
 *
 * @param initial The initial superagent request
 * @param finalProcessing A function that will augment the superagent request to a promise
 * @returns an observable of concatenated events for progress and final value
 */
export function withProgress<T>(
  initial: request.Request,
  finalProcessing: (r: request.Request) => Promise<T>
): Observable<ResultWithProgress<T>> {
  const progressSubject = new Subject<FileProgressLoaderProps>();

  const promise$ = from(
    finalProcessing(
      initial.on("progress", (event) => progressSubject.next(event))
    )
  );

  return concat(
    progressSubject.pipe(
      takeUntil(promise$),
      map<
        FileProgressLoaderProps,
        { status: "progress"; data: FileProgressLoaderProps }
      >((data) => ({
        status: "progress",
        data,
      }))
    ),
    promise$.pipe(
      map<
        T,
        {
          status: "done";
          data: T;
        }
      >((data) => ({ status: "done", data }))
    )
  );
}
